import { BusinessVenueDealCard } from './VenueDealCard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import {
  VenueDealCategoryHappyHour,
  VenueDealStatusActive,
  VenueDealStatusArchived,
  VenueDealStatusPaused,
  VenueWeekdayFriday,
  VenueWeekdayMonday,
  VenueWeekdayThursday,
  VenueWeekdayTuesday,
  VenueWeekdayWednesday,
  type UpsertVenueDealRequest,
  type VenueDeal,
  type VenueDealCategory,
  type VenueWeekday,
} from '@/lib/api/apiTypes';
import { VENUE_DEAL_CATEGORY_FILTER_OPTIONS } from '@radious/catalog/deal-categories';
import { getDealTiming, weekdayOptions } from './deals';
import {
  useCreateVenueDeal,
  useRestoreVenueDealDate,
  useSetVenueDealStatus,
  useUpdateVenueDeal,
  useVenueDeals,
} from './useVenueDeals';
import { trackBusinessDealAction } from '@/lib/analytics/posthog';
import { PlusIcon, TicketPercent } from 'lucide-react-native';
import * as React from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import DateTimePicker, {
  DateTimePickerAndroid,
  type DateTimePickerEvent,
} from '@react-native-community/datetimepicker';

const categories: { label: string; value: VenueDealCategory }[] =
  VENUE_DEAL_CATEGORY_FILTER_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value as VenueDealCategory,
  }));

const defaultDraft: UpsertVenueDealRequest = {
  title: '',
  description: '',
  category: VenueDealCategoryHappyHour,
  active_days: [
    VenueWeekdayMonday,
    VenueWeekdayTuesday,
    VenueWeekdayWednesday,
    VenueWeekdayThursday,
    VenueWeekdayFriday,
  ],
  starts_at_local: '16:00',
  ends_at_local: '19:00',
};

export function BusinessDealsPanel({
  canManage,
  venueID,
}: {
  canManage: boolean;
  venueID: string;
}) {
  const dealsQuery = useVenueDeals(venueID, canManage);
  const createDeal = useCreateVenueDeal(venueID);
  const updateDeal = useUpdateVenueDeal(venueID);
  const setStatus = useSetVenueDealStatus(venueID);
  const restoreDate = useRestoreVenueDealDate(venueID);
  const [draft, setDraft] = React.useState<UpsertVenueDealRequest>(defaultDraft);
  const [editingDeal, setEditingDeal] = React.useState<VenueDeal | null>(null);
  const [archiveDeal, setArchiveDeal] = React.useState<VenueDeal | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const deals = dealsQuery.data?.deals ?? [];
  const visibleDeals = deals.filter((deal) => deal.status !== VenueDealStatusArchived);
  const liveCount = visibleDeals.filter((deal) => getDealTiming(deal).state === 'live').length;
  const activeCount = visibleDeals.filter((deal) => deal.status === VenueDealStatusActive).length;
  const pausedCount = visibleDeals.filter((deal) => deal.status === VenueDealStatusPaused).length;
  const busy =
    createDeal.isPending || updateDeal.isPending || setStatus.isPending || restoreDate.isPending;

  if (!canManage) return null;

  function openCreate() {
    trackBusinessDealAction({ action: 'open_create', venueID });
    setDraft(defaultDraft);
    setEditingDeal(null);
    setError(null);
    setDialogOpen(true);
  }

  function openEdit(deal: VenueDeal) {
    trackBusinessDealAction({
      action: 'open_edit',
      category: deal.category,
      dealID: deal.id,
      venueID,
    });
    setDraft({
      title: deal.title,
      description: deal.description,
      category: deal.category,
      active_days: deal.active_days,
      starts_at_local: deal.starts_at_local,
      ends_at_local: deal.ends_at_local,
    });
    setEditingDeal(deal);
    setError(null);
    setDialogOpen(true);
  }

  async function saveDeal() {
    setError(null);
    if (!draft.title.trim()) return setError('Add a title before saving this deal.');
    if (!draft.description.trim())
      return setError('Add a short description so guests know what is included.');
    if (draft.active_days.length === 0) return setError('Choose at least one day this deal runs.');
    if (draft.starts_at_local >= draft.ends_at_local)
      return setError('Set an end time after the start time.');

    try {
      if (editingDeal) {
        const savedDeal = await updateDeal.mutateAsync({ dealID: editingDeal.id, payload: draft });
        trackBusinessDealAction({
          action: 'updated',
          category: savedDeal.category,
          dealID: savedDeal.id,
          venueID,
        });
      } else {
        const savedDeal = await createDeal.mutateAsync(draft);
        trackBusinessDealAction({
          action: 'created',
          category: savedDeal.category,
          dealID: savedDeal.id,
          venueID,
        });
      }
      setDialogOpen(false);
    } catch {
      setError('Unable to save this deal right now. Check the details and try again.');
    }
  }

  async function confirmArchiveDeal() {
    if (!archiveDeal) return;
    try {
      const savedDeal = await setStatus.mutateAsync({
        dealID: archiveDeal.id,
        status: VenueDealStatusArchived,
      });
      trackBusinessDealAction({
        action: 'archive',
        category: savedDeal.category,
        dealID: savedDeal.id,
        venueID,
      });
      setArchiveDeal(null);
    } catch {
      setError('Unable to archive this deal right now. Try again in a moment.');
    }
  }

  return (
    <View className="gap-3">
      <View className="flex-row items-center justify-between gap-3">
        <View>
          <Text className="text-base font-semibold text-foreground">Deals</Text>
          <Text variant="small" className="text-muted-foreground">
            {liveCount} live now · {activeCount} active · {pausedCount} paused
          </Text>
        </View>
        {canManage ? (
          <Button size="sm" onPress={openCreate}>
            <Icon as={PlusIcon} size={14} className="text-primary-foreground" />
            <Text>New</Text>
          </Button>
        ) : null}
      </View>

      {dealsQuery.isPending ? (
        <View className="rounded-md border border-border bg-card px-4 py-5">
          <Text variant="small" className="text-muted-foreground">
            Loading deals…
          </Text>
        </View>
      ) : null}
      {dealsQuery.isError ? (
        <View className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3">
          <Text variant="small" className="text-destructive">
            Unable to load deals.
          </Text>
        </View>
      ) : null}
      {!dealsQuery.isPending && !dealsQuery.isError && visibleDeals.length === 0 ? (
        <View className="items-center rounded-md border border-border bg-card px-4 py-6">
          <Icon as={TicketPercent} size={22} className="text-muted-foreground" />
          <Text className="mt-2 font-semibold text-foreground">No deals yet</Text>
          <Text variant="small" className="mt-1 text-center text-muted-foreground">
            Add weekly happy hours or specials for this venue.
          </Text>
        </View>
      ) : null}
      {visibleDeals.map((deal) => (
        <BusinessVenueDealCard
          key={deal.id}
          deal={deal}
          busy={busy || !canManage}
          onArchive={() => setArchiveDeal(deal)}
          onEdit={() => openEdit(deal)}
          onRestoreDate={(date) =>
            restoreDate.mutate(
              { dealID: deal.id, date },
              {
                onSuccess: () =>
                  trackBusinessDealAction({
                    action: 'restore_date',
                    category: deal.category,
                    dealID: deal.id,
                    venueID,
                  }),
              }
            )
          }
          onToggleStatus={() => {
            const nextStatus =
              deal.status === VenueDealStatusActive
                ? VenueDealStatusPaused
                : VenueDealStatusActive;
            setStatus.mutate(
              {
                dealID: deal.id,
                status: nextStatus,
              },
              {
                onSuccess: (savedDeal) =>
                  trackBusinessDealAction({
                    action: nextStatus === VenueDealStatusActive ? 'activate' : 'pause',
                    category: savedDeal.category,
                    dealID: savedDeal.id,
                    venueID,
                  }),
              }
            );
          }}
        />
      ))}

      <DealEditorDialog
        draft={draft}
        error={error}
        isOpen={dialogOpen}
        isSaving={busy}
        mode={editingDeal ? 'edit' : 'create'}
        onChange={setDraft}
        onOpenChange={setDialogOpen}
        onSubmit={() => void saveDeal()}
      />
      <AlertDialog
        open={archiveDeal !== null}
        onOpenChange={(open) => !open && setArchiveDeal(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this deal?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {archiveDeal?.title ?? 'this deal'} from public mobile surfaces and hides
              it from the active deals list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row justify-end gap-3">
            <AlertDialogCancel disabled={setStatus.isPending}>
              <Text>Cancel</Text>
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={setStatus.isPending}
              className="bg-destructive active:bg-destructive/90"
              onPress={() => void confirmArchiveDeal()}>
              <Text>{setStatus.isPending ? 'Archiving...' : 'Archive deal'}</Text>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </View>
  );
}

function DealEditorDialog({
  draft,
  error,
  isOpen,
  isSaving,
  mode,
  onChange,
  onOpenChange,
  onSubmit,
}: {
  draft: UpsertVenueDealRequest;
  error: string | null;
  isOpen: boolean;
  isSaving: boolean;
  mode: 'create' | 'edit';
  onChange: (draft: UpsertVenueDealRequest) => void;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
}) {
  const [activeTimePicker, setActiveTimePicker] = React.useState<'start' | 'end' | null>(null);

  function toggleDay(day: VenueWeekday) {
    const nextDays = draft.active_days.includes(day)
      ? draft.active_days.filter((value) => value !== day)
      : [...draft.active_days, day];
    onChange({
      ...draft,
      active_days: weekdayOptions.flatMap((option) =>
        nextDays.includes(option.value) ? [option.value] : []
      ),
    });
  }

  function changeTime(field: 'starts_at_local' | 'ends_at_local', value: Date) {
    onChange({ ...draft, [field]: dateToTimeString(value) });
  }

  function openTimePicker(field: 'start' | 'end') {
    const draftField = field === 'start' ? 'starts_at_local' : 'ends_at_local';

    if (Platform.OS === 'android') {
      DateTimePickerAndroid.open({
        value: timeStringToDate(draft[draftField]),
        mode: 'time',
        is24Hour: false,
        onChange: (event: DateTimePickerEvent, selectedDate?: Date) => {
          if (event.type !== 'set' || !selectedDate) return;
          changeTime(draftField, selectedDate);
        },
      });
      return;
    }

    setActiveTimePicker((current) => (current === field ? null : field));
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent avoidKeyboard className="max-h-[88%] gap-0 overflow-hidden p-0">
        <ScrollView
          className="w-full"
          contentContainerClassName="pb-1"
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <DialogHeader className="border-b border-border px-6 pb-4 pr-12 pt-6">
            <DialogTitle>{mode === 'edit' ? 'Edit deal' : 'New deal'}</DialogTitle>
            <DialogDescription>
              Deals use venue profile media. Use fixtures later for poster-led events.
            </DialogDescription>
          </DialogHeader>

          <View className="gap-4 px-6 py-5">
            <View className="gap-1.5">
              <Text variant="small" className="font-semibold text-foreground">
                Title
              </Text>
              <Input
                value={draft.title}
                onChangeText={(title) => onChange({ ...draft, title })}
                placeholder="Happy hour"
              />
            </View>
            <View className="gap-1.5">
              <Text variant="small" className="font-semibold text-foreground">
                Description
              </Text>
              <Textarea
                value={draft.description}
                onChangeText={(description) => onChange({ ...draft, description })}
                placeholder="Half-price wells and selected snacks"
                className="min-h-24"
              />
            </View>
            <View className="flex-row flex-wrap gap-2">
              {categories.map((category) => (
                <Button
                  key={category.value}
                  size="sm"
                  variant={draft.category === category.value ? 'default' : 'outline'}
                  onPress={() => onChange({ ...draft, category: category.value })}>
                  <Text>{category.label}</Text>
                </Button>
              ))}
            </View>
            <View className="flex-row gap-1">
              {weekdayOptions.map((day) => (
                <Button
                  key={day.value}
                  size="sm"
                  variant={draft.active_days.includes(day.value) ? 'default' : 'outline'}
                  className="flex-1 px-0"
                  onPress={() => toggleDay(day.value)}>
                  <Text>{day.short}</Text>
                </Button>
              ))}
            </View>
            <View className="flex-row gap-3">
              <TimePickerTrigger
                label="Start"
                value={draft.starts_at_local}
                onPress={() => openTimePicker('start')}
              />
              <TimePickerTrigger
                label="End"
                value={draft.ends_at_local}
                onPress={() => openTimePicker('end')}
              />
            </View>
            {Platform.OS === 'ios' && activeTimePicker ? (
              <View className="overflow-hidden rounded-md border border-border bg-background px-2 py-1">
                <DateTimePicker
                  value={timeStringToDate(
                    activeTimePicker === 'start' ? draft.starts_at_local : draft.ends_at_local
                  )}
                  mode="time"
                  display="spinner"
                  is24Hour={false}
                  onChange={(_event: DateTimePickerEvent, selectedDate?: Date) => {
                    if (!selectedDate) return;
                    changeTime(
                      activeTimePicker === 'start' ? 'starts_at_local' : 'ends_at_local',
                      selectedDate
                    );
                  }}
                />
              </View>
            ) : null}
            {error ? (
              <View className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
                <Text variant="small" className="text-destructive">
                  {error}
                </Text>
              </View>
            ) : null}
          </View>

          <DialogFooter className="border-t border-border px-6 py-4">
            <Button variant="outline" onPress={() => onOpenChange(false)}>
              <Text>Cancel</Text>
            </Button>
            <Button disabled={isSaving} onPress={onSubmit}>
              <Text>{isSaving ? 'Saving...' : 'Save deal'}</Text>
            </Button>
          </DialogFooter>
        </ScrollView>
      </DialogContent>
    </Dialog>
  );
}

function TimePickerTrigger({
  label,
  onPress,
  value,
}: {
  label: string;
  onPress: () => void;
  value: string;
}) {
  return (
    <Pressable
      className="flex-1 rounded-md border border-border bg-background px-3 py-2.5 active:bg-accent"
      onPress={onPress}>
      <Text variant="small" className="font-semibold text-foreground">
        {label}
      </Text>
      <Text className="mt-1 text-foreground">{formatTimeLabel(value)}</Text>
    </Pressable>
  );
}

function timeStringToDate(value: string) {
  const [hoursRaw, minutesRaw] = value.split(':');
  const date = new Date();
  date.setHours(Number(hoursRaw) || 0, Number(minutesRaw) || 0, 0, 0);
  return date;
}

function dateToTimeString(value: Date) {
  return `${value.getHours().toString().padStart(2, '0')}:${value.getMinutes().toString().padStart(2, '0')}`;
}

function formatTimeLabel(value: string) {
  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const period = hours >= 12 ? 'PM' : 'AM';
  const normalizedHours = hours % 12 || 12;
  return `${normalizedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}
